import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import type { FormStore } from '../store';
import { createFormStore, validateConfig } from '../store';
import type {
    FieldIdForElements,
    FieldValueForElements,
    FormBuilderConfig,
    FormDataMap,
    FormElementConfig,
    FormStatus,
    FormValuesForElements,
    SubmitCallbacks,
    SubmitResult,
} from '../types/form-builder.types';

// --- Public types ---

export interface UseFormOptions<Elements extends readonly FormElementConfig[] = readonly FormElementConfig[]> {
    readonly config: FormBuilderConfig<Elements>;
    readonly data?: FormDataMap | undefined;
    readonly resetKey?: string | number | undefined;
}

export interface FormController<Elements extends readonly FormElementConfig[] = readonly FormElementConfig[]> {
    readonly getValues: () => FormValuesForElements<Elements>;
    readonly setValue: <FieldId extends FieldIdForElements<Elements>>(
        fieldId: FieldId,
        value: FieldValueForElements<Elements, FieldId>
    ) => void;
    readonly validateField: (fieldId: FieldIdForElements<Elements>) => Promise<string | undefined>;
    readonly submit: (callbacks?: SubmitCallbacks<Elements>) => Promise<SubmitResult<Elements>>;
    readonly reset: () => void;
}

export interface UseFormResult<Elements extends readonly FormElementConfig[] = readonly FormElementConfig[]> {
    readonly form: FormController<Elements>;
    readonly status: FormStatus;
}

// --- Internal bridge (WeakMap avoids type assertions in FormBuilder) ---

const storeMap = new WeakMap<object, FormStore>();

export const getControllerStore = <Elements extends readonly FormElementConfig[]>(
    controller: FormController<Elements>
): FormStore => {
    const store = storeMap.get(controller);
    if (!store) {
        throw new Error('Invalid form controller — was it created by useForm?');
    }
    return store;
};

// --- Hook ---

export function useForm<Elements extends readonly FormElementConfig[]>(
    options: UseFormOptions<Elements>
): UseFormResult<Elements>;
export function useForm({ config, data, resetKey }: UseFormOptions): UseFormResult {
    const store = useMemo(() => {
        validateConfig(config);
        return createFormStore({
            id: config.id,
            elements: config.elements,
            defaultValues: config.defaultValues,
            data,
        });
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- resetKey controls store recreation
    }, [resetKey]);

    useEffect(() => {
        return () => {
            store.destroy();
        };
    }, [store]);

    // Data reactivity — update store when data changes without resetting
    const prevDataRef = useRef(data);
    useEffect(() => {
        if (prevDataRef.current !== data) {
            prevDataRef.current = data;
            store.updateData(data);
        }
    }, [store, data]);

    const subscribe = useCallback((callback: () => void) => store.subscribeForm(callback), [store]);
    const getSnapshot = useCallback(() => store.getFormSnapshot(), [store]);
    const status = useSyncExternalStore(subscribe, getSnapshot);

    const form = useMemo((): FormController => {
        const controller: FormController = {
            getValues: () => store.getValues(),
            setValue: (fieldId, value) => {
                store.setFieldValue(fieldId, value);
            },
            validateField: (fieldId) => store.validateField(fieldId),
            submit: (callbacks) => store.submit(callbacks),
            reset: () => {
                store.reset();
            },
        };
        storeMap.set(controller, store);
        return controller;
    }, [store]);

    return { form, status };
}
